import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, AfterViewChecked, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { TranscationQuery, Transaction, TransactionStatus } from './models/transaction.model';
import { TransactionService } from './services/transaction.service';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { CreateTransactionDialogComponent } from './dialogs/create-transaction-dialog/create-transaction-dialog.component';
import { DeleteTransactionDialogComponent } from './dialogs/delete-transaction-dialog/delete-transaction-dialog.component';
import { SmsResendDialogComponent } from './dialogs/sms-resend-dialog/sms-resend-dialog.component';
import { EmailResendDialogComponent } from './dialogs/email-resend-dialog/email-resend-dialog.component';
import { ViewReportDialogComponent } from './dialogs/view-report-dialog/view-report-dialog.component';
import { Agency } from './models/agency.model';
import { getFullName } from '../../shared/utils/full-name.util';
import { ActionResultDialogComponent } from './dialogs/action-result-dialog/action-result-dialog.component';
import { Subscription } from 'rxjs';
import { SignalRService, TransactionStatusChangeNotification } from '../../core/services/signalr/signalr.sevice';

@Component({
    selector: 'app-transaction',
    templateUrl: './transaction.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        SpinnerComponent,
        CreateTransactionDialogComponent,
        DeleteTransactionDialogComponent,
        SmsResendDialogComponent,
        EmailResendDialogComponent,
        ViewReportDialogComponent,
        ActionResultDialogComponent,
    ]
})
export class TransactionComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
    transactions: Transaction[] = [];
    pagedTransactions: Transaction[] = [];
    currentPage = 1;
    rowsPerPage = 10;
    totalCount = 0;
    totalPages = 0;
    showingFrom = 0;
    showingTo = 0;
    openDropdownIndex: number | null = null;
    searchControl = new FormControl('');
    isLoading = false;
    searchTerm: string | null = null;
    showCreateTransactionDialog = false;
    showDeleteTransactionDialog = false;
    showConfirmSmsResendDialog = false;
    showEmailResendDialog = false;
    showViewReportDialog = false;
    recipientName: string | null = null;
    fileNumber: string | null = null;
    transactionId: number | null = null;
    pdfUrl: string | null = null;
    createdAgency: string | null = null;
    selectedStatuses: number[] = Object.values(TransactionStatus).filter(v => typeof v === 'number') as number[];
    showStatusFilter = false;
    public TransactionStatus = TransactionStatus;
    errorDialogSize: 'sm' | 'md' | 'lg' | 'xl' | '2xl' = 'md';
    errorDialogPosition: 'right' | 'center' = 'center';
    action: 'New Transaction' | 'View Report' | 'Resend SMS' | 'Resend Email' | 'Delete Transaction' = 'New Transaction';
    resultDialogIsError: boolean = false;
    resultDialogShowBack: boolean = false;
    showResultDialog: boolean = false;
    @ViewChild('searchChip') searchChip?: ElementRef;
    // Default for icon
    inputPaddingLeft = 40; 
    private lastSearchTerm: string | null = null;
    private lastChipWidth: number = 0;
    private readonly signalRSubscriptions: Subscription[] = [];

    constructor(
        private readonly transactionService: TransactionService, 
        private readonly cdr: ChangeDetectorRef,
        private readonly signalRService: SignalRService
    ) { }

    ngOnInit() {
        this.loadTransactions();
        this.initializeSignalR();
    }

    ngAfterViewInit() {
        this.updateInputPadding();
    }

    ngAfterViewChecked() {
        // Only update if searchTerm or chip width has changed
        if (this.searchTerm !== this.lastSearchTerm || this.getChipWidth() !== this.lastChipWidth) {
            this.updateInputPadding();
            this.lastSearchTerm = this.searchTerm;
            this.lastChipWidth = this.getChipWidth();
        }
    }

    private getChipWidth(): number {
        return this.searchChip && this.searchChip.nativeElement ? this.searchChip.nativeElement.offsetWidth : 0;
    }

    updateInputPadding() {
        setTimeout(() => {
            if (this.searchChip && this.searchChip.nativeElement && this.searchTerm) {
                this.inputPaddingLeft = 40 + this.searchChip.nativeElement.offsetWidth + 8;
            } else {
                this.inputPaddingLeft = 40;
            }
            this.cdr.detectChanges();
        });
    }

    onSearch() {
        const value = this.searchControl.value;
        if (value && value.length >= 2) {
            this.searchTerm = value;
            this.currentPage = 1;
            this.loadTransactions(value);
            // Update padding when searchTerm changes
            this.updateInputPadding(); 
        }
    }

    clearSearch() {
        this.searchTerm = null;
        this.searchControl.setValue('');
        this.loadTransactions();
        // Update padding when searchTerm changes
        this.updateInputPadding();
    }

    isSearchEnabled(): boolean {
        const value = this.searchControl.value;
        return value !== null && value.length >= 2;
    }

    loadTransactions(searchTerm?: string) {
        this.isLoading = true;
        const pageIndex = this.currentPage - 1; // Convert to 0-based index for API
        const allStatuses = Object.values(this.TransactionStatus).filter(v => typeof v === 'number') as number[];
        const filter: TranscationQuery = {
            searchTerm: searchTerm ?? this.searchTerm ?? undefined,
            statuses: this.selectedStatuses.length === allStatuses.length ? null as any : this.selectedStatuses
        };
        this.transactionService.getTransactions(pageIndex, this.rowsPerPage, filter)
            .subscribe({
                next: (response) => {
                    this.transactions = response.data;
                    this.totalCount = response.count;
                    this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage);
                    this.updatePagination();
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                }
            });
    }

    updatePagination() {
        this.showingFrom = (this.currentPage - 1) * this.rowsPerPage + 1;
        this.showingTo = Math.min(this.currentPage * this.rowsPerPage, this.totalCount);
        this.pagedTransactions = this.transactions;
    }

    changePage(page: number) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page;
            this.loadTransactions(this.searchControl.value || undefined);
        }
    }

    changeRowsPerPage(event: Event) {
        const select = event.target as HTMLSelectElement;
        this.rowsPerPage = parseInt(select.value, 10);
        this.currentPage = 1;
        this.loadTransactions(this.searchControl.value || undefined);
    }

    getStatusClass(status: string | TransactionStatus): string {
        const statusEnum = typeof status === 'string'
            ? TransactionStatus[status as keyof typeof TransactionStatus]
            : status;

        switch (statusEnum) {
            case TransactionStatus.Completed:
                return 'bg-green-100 text-green-800 border-green-200';
            case TransactionStatus.Pending:
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case TransactionStatus.Failed:
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    }


    getStatusText(status: string | number): string {
        if (typeof status === 'string') {
            const value = TransactionStatus[status as keyof typeof TransactionStatus];
            if (typeof value === 'number') {
                return status; // It's a valid key, return it
            }
        }

        if (typeof status === 'number') {
            return TransactionStatus[status] ?? 'Unknown';
        }

        return 'Unknown';
    }

    getStatusEnumValues(): Array<{ key: string; value: number }> {
        return Object.keys(TransactionStatus)
            .filter(key => typeof TransactionStatus[key as keyof typeof TransactionStatus] === 'number')
            .map(key => ({
                key,
                value: TransactionStatus[key as keyof typeof TransactionStatus] as number
            }));
    }

    openNewTransactionDialog() {
        this.showCreateTransactionDialog = true;
    }

    closeNewTransactionDialog() {
        this.showCreateTransactionDialog = false;
    }

    onTransactionCreated(data: { fileNumber: string; agency: Agency | null }) {
        this.showCreateTransactionDialog = false;
        this.fileNumber = data.fileNumber;
        this.createdAgency = data.agency != null ? `${data.agency.accountNumber} / ${data.agency.locationName}` : '';
        this.errorDialogSize = 'lg'
        this.errorDialogPosition = 'right'
        this.currentPage = 1;
        this.resultDialogIsError = false;
        this.searchTerm = null;
        this.action = 'New Transaction';
        this.showResultDialog = true;
        this.searchControl.setValue('');
        this.selectedStatuses = Object.values(this.TransactionStatus).filter(v => typeof v === 'number') as number[];
        this.loadTransactions();
    }

    onTransactionCreateError() {
        this.resultDialogIsError = true;
        this.resultDialogShowBack = true;
        this.showResultDialog = true;
        this.errorDialogSize = 'lg'
        this.errorDialogPosition = 'right'
        this.action = 'New Transaction';
    }

    onResultDialogBack() {
        this.showResultDialog = false;
        this.showCreateTransactionDialog = true;
    }

    onResultDialogClose() {
        this.showResultDialog = false;
        this.showCreateTransactionDialog = false;
        this.fileNumber = null;
    }

    deleteTransaction(index: number) {
        this.fileNumber = this.transactions[index]?.fileNumber?.toString() || null;
        this.transactionId = this.transactions[index]?.id;
        this.showDeleteTransactionDialog = true;
    }

    closeDeleteTransactionDialog() {
        this.showDeleteTransactionDialog = false;
        this.fileNumber = null;
        this.transactionId = null
    }

    confirmDeleteTransaction() {
        this.loadTransactions();
        this.closeDeleteTransactionDialog();
    }

    resendSms(index: number) {
        const transaction = this.transactions[index];
        if (transaction && transaction.phoneNumberProvided) {
            this.recipientName = getFullName(transaction);
            this.transactionId = transaction.id;
            this.showConfirmSmsResendDialog = true;
        }
    }

    closeConfirmSmsResendDialog() {
        this.showConfirmSmsResendDialog = false;
        this.recipientName = null;
        this.transactionId = null;
    }

    confirmResendSms() {
        this.closeConfirmSmsResendDialog();
    }

    onSmsResendError() {
        this.resultDialogIsError = true;
        this.resultDialogShowBack = false;
        this.showResultDialog = true;
        this.errorDialogSize = 'md'
        this.errorDialogPosition = 'center'
        this.showConfirmSmsResendDialog = false;
        this.action = 'Resend SMS';
    }


    onEmailResendError() {
        this.resultDialogIsError = true;
        this.resultDialogShowBack = false;
        this.showResultDialog = true;
        this.errorDialogSize = 'md'
        this.errorDialogPosition = 'center'
        this.showEmailResendDialog = false;
        this.action = 'Resend Email';
    }

    onDeleteTransactionError() {
        this.resultDialogIsError = true;
        this.resultDialogShowBack = false;
        this.showResultDialog = true;
        this.errorDialogSize = 'md'
        this.errorDialogPosition = 'center'
        this.showDeleteTransactionDialog = false;
        this.action = 'Delete Transaction';
    }

    resendEmail(index: number) {
        const transaction = this.transactions[index];
        if (transaction) {
            this.recipientName = getFullName(transaction);
            this.fileNumber = transaction.fileNumber;
            this.transactionId = transaction.id;
            this.showEmailResendDialog = true;
        }
    }

    closeEmailResendDialog() {
        this.showEmailResendDialog = false;
        this.recipientName = null;
        this.fileNumber = null;
        this.transactionId = null;
    }

    confirmResendEmail() {
        this.closeEmailResendDialog();
    }

    viewReport(index: number): void {
        this.isLoading = true;
        const transaction = this.transactions[index];
        this.recipientName = `${transaction.firstName} ${transaction.lastName}`;
        this.fileNumber = transaction.fileNumber;

        this.transactionService.getIdentityVerificationReport(transaction.id).subscribe({
            next: (blob) => {
                const blobUrl = URL.createObjectURL(blob);
                this.pdfUrl = blobUrl;
                this.showViewReportDialog = true;
                this.isLoading = false;
            },
            error: () => {
                this.resultDialogIsError = true;
                this.resultDialogShowBack = false;
                this.showResultDialog = true;
                this.errorDialogSize = 'md'
                this.errorDialogPosition = 'center'
                this.showDeleteTransactionDialog = false;
                this.showViewReportDialog = false;
                this.isLoading = false;
                this.action = 'View Report';
            }
        });
    }

    closeViewReportDialog() {
        this.showViewReportDialog = false;
        this.recipientName = null;
        this.fileNumber = null;
        this.pdfUrl = null;
    }

    confirmViewReport() {
        this.closeViewReportDialog();
    }

    getFullName(transaction: Transaction): string {
        return getFullName(transaction);
    }

    toggleStatusFilter() {
        this.showStatusFilter = !this.showStatusFilter;
    }

    onStatusesChange(statuses: number[]) {
        this.selectedStatuses = statuses;
        this.currentPage = 1;
    }

    handleStatusCheckboxChange(statusValue: number, event: Event) {
        const input = event.target as HTMLInputElement;
        const isChecked = input.checked;
        let updated: number[];
        if (isChecked) {
            updated = Array.from(new Set([...this.selectedStatuses, statusValue]));
        } else {
            updated = this.selectedStatuses.filter(s => s !== statusValue);
        }
        this.onStatusesChange(updated);
    }   

    selectAllStatuses() {
        const allStatuses = Object.values(this.TransactionStatus).filter(v => typeof v === 'number') as number[];
        this.onStatusesChange(allStatuses);
    }

    unselectAllStatuses() {
        this.onStatusesChange([]);
    }    
    
    isAllStatusesSelected(): boolean {
        const allStatuses = Object.values(this.TransactionStatus).filter(v => typeof v === 'number') as number[];
        return allStatuses.length === this.selectedStatuses.length && allStatuses.every(status => this.selectedStatuses.includes(status));
    }

    isSomeStatusesSelected(): boolean {
        return this.selectedStatuses.length > 0 && !this.isAllStatusesSelected();
    }

    toggleSelectAll() {
        if (this.isAllStatusesSelected()) {
            this.unselectAllStatuses();
        } else {
            this.selectAllStatuses();
        }
    }

    closeStatusFilterAndRefresh() {
        if (this.selectedStatuses.length > 0) {
            this.showStatusFilter = false;
            this.currentPage = 1;
            this.loadTransactions(this.searchTerm || undefined);
        }
    }

    isCloseButtonEnabled(): boolean {
        return this.selectedStatuses.length > 0;
    }

    private initializeSignalR(): void {
        // Start SignalR connection
        this.signalRService.startConnection();

        // Subscribe to status changes
        const statusSubscription = this.signalRService.transactionStatusChange$.subscribe(
            (notification: TransactionStatusChangeNotification | null) => {
                if (notification) {
                    this.handleStatusChange(notification);
                }
            }
        );

        this.signalRSubscriptions.push(statusSubscription);
    }

    private handleStatusChange(notification: TransactionStatusChangeNotification): void {
        // Find and update the transaction status
        const transactionIndex = this.transactions.findIndex(t => t.id === notification.transactionId);
        if (transactionIndex !== -1) {
            this.transactions[transactionIndex].status = notification.newStatus;
            
            // Update pagination if needed
            this.updatePagination();
            
            // Trigger change detection
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy(): void {
        // Clean up SignalR subscriptions
        this.signalRSubscriptions.forEach(subscription => subscription.unsubscribe());
        
        // Force stop SignalR connection to prevent 404 errors during navigation
        this.signalRService.forceStopConnection();
    }
} 